import { Box } from "@material-ui/core"
import React, { FunctionComponent, useMemo } from "react"
import usePromise from "react-use-promise"
import { useData } from "../common/DataProvider"
import { FieldHelpTooltip } from "../feature-form/FieldHelpTooltip"
import { SelectField } from "../feature-form/SelectField"

/** DisplayFieldSetting component props */
interface DisplayFieldSettingProps {}

/**
 * The "displayField" is a feature layer definition that tells the app
 * which attribute/property to use as the feature's title (or display field).
 * This is then used in the list
 */
export const DisplayFieldSetting: FunctionComponent<DisplayFieldSettingProps> = () => {
  /** Grab the active feature layer */
  const {
    activeFeatureLayer: [featureLayer],
  } = useData()

  /** Grab the possible fields */
  const [possibleFields] = usePromise<undefined | __esri.Field[]>(() => {
    if (!featureLayer) return Promise.resolve(undefined)
    return featureLayer
      ?.when()
      .then((loadedLayer: __esri.FeatureLayer) => loadedLayer.fields)
  }, [featureLayer])

  /** Generate a SchemaField definition to match the SelectField component */
  const field = useMemo(
    () =>
      possibleFields && {
        name: "displayField",
        alias: "Title field",
        description:
          "Select the field from the schema that represets the feature's title/name.",
        options: possibleFields?.map(({ name }) => name).join(", "),
        required: true,
      },
    [possibleFields]
  )

  return field ? (
    <>
      <SelectField field={field} defaultValue={featureLayer?.displayField} />
      <Box mt={0.5}>
        <FieldHelpTooltip
          title={field.description}
          PopperProps={{ placement: "right" }}
        />
      </Box>
    </>
  ) : null
}
